import { Button, Combobox, TextInput, Menu } from '@acast/decibel';
import { CopyLightGlyph } from '@acast/decibel-glyphs';
import flattenArray from 'lodash.flatten';
import T from 'prop-types';
import React, { useRef, useState } from 'react';
import clipboardCopy from 'clipboard-copy';

const TAG_DELIMITERS = ['\n', ','];

const split = delimiter => str => str.split(delimiter);

const splitStringIntoTags = (stringToSplit, delimiters = []) =>
  delimiters.reduce((tags, delimiter) => flattenArray(tags.map(split(delimiter))), [stringToSplit]);

const getFilteredTokens = ({ tokens, options, restrictToOptions } = {}) => {
  if (!restrictToOptions || !Array.isArray(options)) {
    return tokens;
  }

  return tokens.filter(token => options.includes(token));
};

const trimString = str => str.trimStart().trimEnd();

const stringToOption = str => {
  const value = trimString(str);
  return { value, label: value };
};

const optionToString = option => option.value || '';

const TokenInput = props => {
  const { restrictToOptions, onChange, placeholder } = props;

  const inputRef = useRef(null);
  const [inputValue, setInputValue] = useState('');

  const tokens = getFilteredTokens(props).map(stringToOption);
  const options = props.options.map(stringToOption);
  const clipboardText = tokens.map(optionToString).join('\n');

  const inputValueExistsInTokens =
    !restrictToOptions && tokens.findIndex(token => optionToString(token) === inputValue) !== -1;

  const allowedToCreateOption =
    !restrictToOptions && inputValue.trim() !== '' && !inputValueExistsInTokens;

  let noOptionsMessage =
    restrictToOptions || allowedToCreateOption ? null : 'Type something and press enter';

  if (inputValueExistsInTokens) {
    noOptionsMessage = 'Item already selected';
  }

  function emitChange(newTokens) {
    if (typeof onChange === 'function') {
      onChange(newTokens.map(optionToString));
    }
  }

  function handlePaste(e) {
    e.preventDefault();

    const text =
      e.clipboardData &&
      typeof e.clipboardData.getData === 'function' &&
      e.clipboardData.getData('Text');

    if (!text) {
      return;
    }

    const pastedTags = splitStringIntoTags(text, TAG_DELIMITERS)
      .filter(name => name && name.trim())
      .map(stringToOption);

    emitChange([...tokens, ...pastedTags]);
  }

  function createOption() {
    if (allowedToCreateOption) {
      emitChange([...tokens, stringToOption(inputValue)]);
      setInputValue('');
      inputRef.current.focus();
    }
  }

  function handleKeyDown({ key }) {
    if (key === 'Enter') {
      createOption();
    }
  }

  const showCopyButton = !!tokens.length;

  return (
    <Combobox
      value={tokens}
      inputValue={inputValue}
      onChange={emitChange}
      onInputChange={setInputValue}
      onPaste={handlePaste}
      onKeyDown={handleKeyDown}
      openOnFocus={restrictToOptions}
      multiple
      input={
        <Combobox.Input
          ref={inputRef}
          placeholder={placeholder}
          addonEnd={
            showCopyButton ? (
              <TextInput.Addon pr={1} pt="3px" alignItems="flex-start">
                <Button
                  variant="tertiary"
                  size="small"
                  glyph={CopyLightGlyph}
                  title="Copy to clipboard"
                  onClick={() => {
                    clipboardCopy(clipboardText);
                  }}
                />
              </TextInput.Addon>
            ) : null
          }
        />
      }
    >
      <Combobox.List noOptionsMessage={noOptionsMessage}>
        {options.map(option => (
          <Combobox.Option key={option.value} value={option.value} label={option.label} />
        ))}
      </Combobox.List>
      {allowedToCreateOption && (
        // eslint-disable-next-line jsx-a11y/aria-role
        <Menu.Item role={null} tabIndex="0" onClick={createOption}>
          Add "{inputValue}"
        </Menu.Item>
      )}
    </Combobox>
  );
};

TokenInput.propTypes = {
  tokens: T.array,
  options: T.array,
  restrictToOptions: T.bool,
  placeholder: T.string,
  onChange: T.func,
};

TokenInput.defaultProps = {
  tokens: [],
  options: [],
  restrictToOptions: false,
  placeholder: 'Add new tag',
  onChange: () => {},
};

export default TokenInput;
